import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import {
  Button,
  Collapse,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react';
import { EVENT_NAMES } from '@web-app/context/AnalyticsContext';
import { useEffect, useState } from 'react';

import {
  CallQualitySurveyData,
  CallQualitySurveyModalProps,
  RatingValue,
} from './types';

const CallQualitySurveyModalContent: React.FC<CallQualitySurveyModalProps> = ({
  onSubmit,
  onSkip,
  dailyRoomId,
  healthieAppointmentId,
  duration,
  callExperienceVersion,
  user,
  trackEvent,
}) => {
  const isProvider = !!user;

  // Survey state
  const [surveyData, setSurveyData] = useState<CallQualitySurveyData>({
    callQualityRating: null,
    callQualityFeedback: '',
    providerExperienceRating: null,
    providerExperienceFeedback: '',
  });

  // Comment section expansion state
  const [providerCommentExpanded, setProviderCommentExpanded] = useState(false);
  const [callQualityCommentExpanded, setCallQualityCommentExpanded] =
    useState(false);

  // Track view event when modal mounts
  useEffect(() => {
    trackEvent(EVENT_NAMES.VIEW_CALL_QUALITY_SURVEY, {
      userType: isProvider ? 'provider' : 'patient',
      dailyRoomId,
      healthieAppointmentId,
      duration,
      callExperienceVersion,
    });
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSkip = () => {
    trackEvent(EVENT_NAMES.SKIP_CALL_QUALITY_SURVEY, {
      userType: isProvider ? 'provider' : 'patient',
      dailyRoomId,
      healthieAppointmentId,
      duration,
      step: 1,
      callExperienceVersion,
    });
    onSkip();
  };

  const handleSubmit = () => {
    // For patients, track provider experience survey only if answered
    if (!isProvider && surveyData.providerExperienceRating !== null) {
      trackEvent(EVENT_NAMES.SUBMIT_PROVIDER_EXPERIENCE_SURVEY, {
        providerExperienceRating: surveyData.providerExperienceRating,
        providerExperienceFeedback: surveyData.providerExperienceFeedback,
        userType: 'patient',
        dailyRoomId,
        healthieAppointmentId,
        duration,
        callExperienceVersion,
      });
    }

    // Track call quality survey only if answered
    if (surveyData.callQualityRating !== null) {
      trackEvent(EVENT_NAMES.SUBMIT_CALL_QUALITY_SURVEY, {
        callQualityRating: surveyData.callQualityRating,
        callQualityFeedback: surveyData.callQualityFeedback,
        userType: isProvider ? 'provider' : 'patient',
        dailyRoomId,
        healthieAppointmentId,
        duration,
        callExperienceVersion,
      });
    }

    onSubmit();
  };

  // Validation: providers need call quality rating, patients need at least one rating
  const canSubmit = isProvider
    ? surveyData.callQualityRating !== null
    : surveyData.providerExperienceRating !== null ||
      surveyData.callQualityRating !== null;

  // Rating button component
  const RatingButton = ({
    value,
    isSelected,
    onClick,
  }: {
    value: number;
    isSelected: boolean;
    onClick: () => void;
  }) => (
    <Button
      onClick={onClick}
      size="md"
      variant={isSelected ? 'solid' : 'outline'}
      bg={isSelected ? 'orange.500' : 'transparent'}
      color={isSelected ? 'white' : 'text.default'}
      borderColor={isSelected ? 'orange.500' : 'border.default'}
      _hover={{
        bg: isSelected ? 'orange.600' : 'surface.light',
      }}
      minW="48px"
      h="48px"
      borderRadius="8px"
    >
      {value}
    </Button>
  );

  return (
    <Modal isOpen={true} onClose={handleSkip} isCentered size="md">
      <ModalOverlay />
      <ModalContent
        bg="surface.default"
        overflow="hidden"
        borderColor="border.default"
        borderWidth={1}
        borderRadius={10}
        maxWidth="560px"
        color="text.default"
      >
        <ModalHeader as={VStack} mt={5} pb={2} gap={1} alignItems="center">
          <Heading size="md" fontFamily="Plus Jakarta Sans" textAlign="center">
            We&apos;d love your feedback to help us improve your experience.
          </Heading>
        </ModalHeader>

        <ModalBody as={VStack} gap={6} position="relative" mb={3} px={8}>
          {/* Provider Experience (patients only) */}
          {!isProvider && (
            <VStack align="stretch" spacing={3} w="100%">
              <FormControl>
                <FormLabel fontSize="md" mb={3} fontWeight="medium">
                  How was your overall experience with your provider today?
                </FormLabel>
                <VStack align="stretch" spacing={3}>
                  <HStack justify="space-between" spacing={2}>
                    {[1, 2, 3, 4, 5].map((rating) => (
                      <RatingButton
                        key={rating}
                        value={rating}
                        isSelected={
                          surveyData.providerExperienceRating === rating
                        }
                        onClick={() =>
                          setSurveyData({
                            ...surveyData,
                            providerExperienceRating: rating as RatingValue,
                          })
                        }
                      />
                    ))}
                  </HStack>
                  <Flex justify="space-between" px={1}>
                    <Text fontSize="sm" color="text.light">
                      Poor
                    </Text>
                    <Text fontSize="sm" color="text.light">
                      Excellent
                    </Text>
                  </Flex>
                </VStack>
              </FormControl>

              <Button
                variant="link"
                size="sm"
                color="green.600"
                leftIcon={
                  providerCommentExpanded ? (
                    <ChevronUpIcon />
                  ) : (
                    <ChevronDownIcon />
                  )
                }
                onClick={() =>
                  setProviderCommentExpanded(!providerCommentExpanded)
                }
                justifyContent="flex-start"
                fontWeight="normal"
              >
                Add comment (optional)
              </Button>

              <Collapse in={providerCommentExpanded} animateOpacity>
                <Textarea
                  value={surveyData.providerExperienceFeedback}
                  onChange={(e) =>
                    setSurveyData({
                      ...surveyData,
                      providerExperienceFeedback: e.target.value,
                    })
                  }
                  placeholder="Share your feedback about your visit..."
                  bg="surface.light"
                  resize="vertical"
                  minH="80px"
                  _placeholder={{
                    color: 'whiteAlpha.500',
                  }}
                />
              </Collapse>
            </VStack>
          )}

          {/* Call Quality (all users) */}
          <VStack align="stretch" spacing={3} w="100%">
            <FormControl>
              <FormLabel fontSize="md" mb={3} fontWeight="medium">
                How would you rate the quality of the video and audio on your
                call today?
              </FormLabel>
              <VStack align="stretch" spacing={3}>
                <HStack justify="space-between" spacing={2}>
                  {[1, 2, 3, 4, 5].map((rating) => (
                    <RatingButton
                      key={rating}
                      value={rating}
                      isSelected={surveyData.callQualityRating === rating}
                      onClick={() =>
                        setSurveyData({
                          ...surveyData,
                          callQualityRating: rating as RatingValue,
                        })
                      }
                    />
                  ))}
                </HStack>
                <Flex justify="space-between" px={1}>
                  <Text fontSize="sm" color="text.light">
                    Poor
                  </Text>
                  <Text fontSize="sm" color="text.light">
                    Excellent
                  </Text>
                </Flex>
              </VStack>
            </FormControl>

            <Button
              variant="link"
              size="sm"
              color="green.600"
              leftIcon={
                callQualityCommentExpanded ? (
                  <ChevronUpIcon />
                ) : (
                  <ChevronDownIcon />
                )
              }
              onClick={() =>
                setCallQualityCommentExpanded(!callQualityCommentExpanded)
              }
              justifyContent="flex-start"
              fontWeight="normal"
            >
              Add comment (optional)
            </Button>

            <Collapse in={callQualityCommentExpanded} animateOpacity>
              <Textarea
                value={surveyData.callQualityFeedback}
                onChange={(e) =>
                  setSurveyData({
                    ...surveyData,
                    callQualityFeedback: e.target.value,
                  })
                }
                placeholder="Share any details about the call quality..."
                bg="surface.light"
                resize="vertical"
                minH="80px"
                _placeholder={{
                  color: 'whiteAlpha.500',
                }}
              />
            </Collapse>
          </VStack>
        </ModalBody>

        <ModalFooter justifyContent="center" mb={5} gap={4}>
          <Button
            variant="outline"
            onClick={handleSkip}
            _hover={{
              color: 'green.600',
              bgColor: 'white',
            }}
            color="text.default"
          >
            Skip
          </Button>

          <Button
            variant="solid"
            bg="orange.500"
            isDisabled={!canSubmit}
            onClick={handleSubmit}
            _hover={{
              bg: 'orange.600',
            }}
            color="white"
          >
            Submit Feedback
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export { CallQualitySurveyModalContent };
